// Firebase Firestore service for Last 10% Execution tracking
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    query,
    where,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

const USER_METRICS_COLLECTION = 'user_metrics';
const GOALS_COLLECTION = 'goals';

// ─── User Metrics ─────────────────────────────────────────────────────────────

/**
 * Retrieve or initialize the user-level Last 10% metrics document for a specific calendar.
 * @param {string} userId
 * @param {string} calendarId
 * @returns {Promise<Object>}
 */
export const getUserMetrics = async (userId, calendarId) => {
    if (!userId) throw new Error('User ID is required');
    if (!calendarId) throw new Error('Calendar ID is required');

    // Use composite key: userId_calendarId to store per-calendar metrics
    const docId = `${userId}_${calendarId}`;
    const ref = doc(db, USER_METRICS_COLLECTION, docId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        const defaults = {
            userId,
            calendarId,
            totalEnteredLast10: 0,
            totalCompletedFromLast10: 0,
            totalAbandonedInLast10: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };
        await setDoc(ref, defaults);
        return { ...defaults, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    }

    const data = snap.data();
    return {
        ...data,
        createdAt: data.createdAt?.toDate().toISOString(),
        updatedAt: data.updatedAt?.toDate().toISOString()
    };
};

/**
 * Compute the user's Finish Score for a specific calendar by querying goals directly.
 * This avoids permission issues with the user_metrics collection.
 * Finish Score = completedFromLast10 / totalEnteredLast10  (expressed as 0–100)
 * Returns null when no entry has yet been recorded (avoids misleading 0%).
 *
 * @param {string} userId
 * @param {string} calendarId
 * @returns {Promise<{ score: number|null, totalEnteredLast10: number, totalCompletedFromLast10: number, totalAbandonedInLast10: number }>}
 */
export const getFinishScore = async (userId, calendarId) => {
    if (!userId) throw new Error('User ID is required');
    if (!calendarId) throw new Error('Calendar ID is required');

    try {
        // Query all goals for this calendar (simple query, no composite index needed)
        const goalsRef = collection(db, GOALS_COLLECTION);
        const q = query(
            goalsRef,
            where('calendarId', '==', calendarId)
        );

        const querySnapshot = await getDocs(q);
        const allGoals = [];

        querySnapshot.forEach((docSnap) => {
            allGoals.push({
                id: docSnap.id,
                ...docSnap.data()
            });
        });

        // Filter in JavaScript to avoid needing a composite index
        const goalsInLast10 = allGoals.filter(g => g.last10EnteredAt != null);

        // Calculate metrics from goals
        const totalEnteredLast10 = goalsInLast10.length;
        const totalCompletedFromLast10 = goalsInLast10.filter(g => g.completedFromLast10 === true).length;
        const totalAbandonedInLast10 = goalsInLast10.filter(g => g.abandonedInLast10 === true).length;

        return {
            score: totalEnteredLast10 > 0 ? Math.round((totalCompletedFromLast10 / totalEnteredLast10) * 100) : null,
            totalEnteredLast10,
            totalCompletedFromLast10,
            totalAbandonedInLast10
        };
    } catch (error) {
        console.error('Error calculating Finish Score from goals:', error);
        throw error;
    }
};

// ─── Last 10% Entry ───────────────────────────────────────────────────────────

/**
 * Called when a goal's completion first crosses the 80% threshold.
 * - Stamps goal doc with last10EnteredAt
 * - Increments user metric counter for the specific calendar
 *
 * @param {string} goalId
 * @param {string} userId
 * @param {string} calendarId
 * @param {number} completionPercentage   current pct at time of entry
 * @returns {Promise<void>}
 */
export const recordLast10Entry = async (goalId, userId, calendarId, completionPercentage = 80) => {
    if (!goalId || !userId) throw new Error('Goal ID and User ID are required');
    if (!calendarId) throw new Error('Calendar ID is required');

    const now = serverTimestamp();

    // Stamp the goal document
    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    await updateDoc(goalRef, {
        last10EnteredAt: now,
        timestampLastActivity: now,
        finishModeActivated: false,
        completedFromLast10: false,
        abandonedInLast10: false,
        daysStalled: 0,
        percentageAtLast10Entry: completionPercentage,
        updatedAt: now
    });
};


// ─── Finish Mode ──────────────────────────────────────────────────────────────

/**
 * Toggle Finish Mode activation state on a goal.
 *
 * @param {string} goalId
 * @param {boolean} activated
 * @param {string} userId   (unused currently, reserved for audit trail)
 * @returns {Promise<void>}
 */
export const setFinishModeActivated = async (goalId, activated, userId) => {
    if (!goalId) throw new Error('Goal ID is required');

    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    const updates = {
        finishModeActivated: activated,
        updatedAt: serverTimestamp()
    };

    if (activated) {
        updates.finishModeActivatedAt = serverTimestamp();
    }

    await updateDoc(goalRef, updates);
};

// ─── Activity Timestamp ───────────────────────────────────────────────────────

/**
 * Update the goal's last-activity timestamp (call whenever a task is completed
 * while the goal is in Last 10% state).
 *
 * @param {string} goalId
 * @returns {Promise<void>}
 */
export const touchLastActivity = async (goalId) => {
    if (!goalId) throw new Error('Goal ID is required');

    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    await updateDoc(goalRef, {
        timestampLastActivity: serverTimestamp(),
        updatedAt: serverTimestamp()
    });
};

// ─── Completion from Last 10% ─────────────────────────────────────────────────

/**
 * Record that a goal was completed after entering Last 10% state.
 * Updates both the goal doc and the user aggregate for the specific calendar.
 *
 * @param {string} goalId
 * @param {string} userId
 * @param {string} calendarId
 * @returns {Promise<void>}
 */
export const recordLast10Completion = async (goalId, userId, calendarId) => {
    if (!goalId || !userId) throw new Error('Goal ID and User ID are required');
    if (!calendarId) throw new Error('Calendar ID is required');

    const now = serverTimestamp();

    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    await updateDoc(goalRef, {
        completedFromLast10: true,
        updatedAt: now
    });
};


// ─── Abandonment ──────────────────────────────────────────────────────────────

/**
 * Mark a goal as abandoned in the Last 10% phase.
 * Called by a scheduled check (or manually) if daysStalled exceeds threshold.
 *
 * @param {string} goalId
 * @param {string} userId
 * @param {string} calendarId
 * @param {number} daysStalled
 * @returns {Promise<void>}
 */
export const recordLast10Abandonment = async (goalId, userId, calendarId, daysStalled = 0) => {
    if (!goalId || !userId) throw new Error('Goal ID and User ID are required');
    if (!calendarId) throw new Error('Calendar ID is required');

    const now = serverTimestamp();

    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    await updateDoc(goalRef, {
        abandonedInLast10: true,
        daysStalled,
        updatedAt: now
    });
};


// ─── Days Stalled Calculation ─────────────────────────────────────────────────

/**
 * Calculate how many days a goal has been stalled in Last 10% state.
 *
 * @param {Object} goal  Goal document (must have timestampLastActivity or last10EnteredAt)
 * @returns {number} days stalled (0 if not in last10)
 */
export const calcDaysStalled = (goal) => {
    if (!goal.last10EnteredAt) return 0;

    const referenceIso = goal.timestampLastActivity || goal.last10EnteredAt;
    const reference = new Date(referenceIso);
    const now = new Date();
    const diffMs = now - reference;
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

/**
 * Determine whether a goal should be flagged as abandoned based on a
 * configurable staleness threshold (default: 7 days).
 *
 * @param {Object} goal
 * @param {number} stallThresholdDays
 * @returns {boolean}
 */
export const isStalled = (goal, stallThresholdDays = 7) => {
    if (!goal.last10EnteredAt || goal.status === 'completed' || goal.completedFromLast10) {
        return false;
    }
    return calcDaysStalled(goal) >= stallThresholdDays;
};
